/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * App - Main Application Router
 * ============================================================================
 *
 * Root component with routing configuration for auth and SuperAdmin portal.
 *
 * ============================================================================
 */

import { lazy, Suspense, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layouts/AppLayout';
import { SuperAdminLayout } from '@/components/layouts/SuperAdminLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RoleGuard } from '@/components/auth/RoleGuard';
import { CurrencyInitializer } from '@/components/providers/CurrencyInitializer';
import { SessionRefresher } from '@/components/providers/SessionRefresher';
import { CoreXLoader } from '@/components/ui/CoreXLoader';
import { SlowOverlayProvider, SlowOverlayContext } from '@/hooks/useSlowTransaction';
import { useFavicon } from '@/hooks/useFavicon';
import { useGlobalValidationInterceptor } from '@/hooks/useGlobalValidationInterceptor';

// Business Modules (lazy loaded — preload namespace alongside chunk)
import i18n from '@/lib/i18n';
const VentaModule = lazy(async () => {
  await i18n.loadNamespaces('venta');
  return import('@/modules/venta');
});
import {
  BootstrapPage,
  LoginPage,
  MFASetupPage,
  MFAVerifyPage,
  MfaSetupRequiredPage,
  MfaRecommendPage,
  PasswordResetRequestPage,
  PasswordResetPage,
  AcceptInvitePage
} from '@/pages/auth';

// SuperAdmin Pages
import SuperAdminDashboard from '@/pages/superadmin/DashboardPage';
import OrganizationsPage from '@/pages/superadmin/OrganizationsPage';
import OrganizationDetailPage from '@/pages/superadmin/OrganizationDetailPage';
import CreateOrganizationPage from '@/pages/superadmin/CreateOrganizationPage';
import EditOrganizationPage from '@/pages/superadmin/EditOrganizationPage';
import RegistrationsPage from '@/pages/superadmin/RegistrationsPage';
// ARCHIVED: Legacy subscription plans replaced by modular pricing
// import PlansPage from '@/pages/superadmin/PlansPage';
// import EditPlanPage from '@/pages/superadmin/EditPlanPage';
import PaymentsPage from '@/pages/superadmin/PaymentsPage';
import PartnersPage from '@/pages/superadmin/PartnersPage';
import PartnerDetailPage from '@/pages/superadmin/PartnerDetailPage';
import CreatePartnerPage from '@/pages/superadmin/CreatePartnerPage';
import EditPartnerPage from '@/pages/superadmin/EditPartnerPage';
import WithdrawalsPage from '@/pages/superadmin/WithdrawalsPage';
import CommissionsPage from '@/pages/superadmin/CommissionsPage';
import InviteCodesPage from '@/pages/superadmin/InviteCodesPage';
import SupportToolsPage from '@/pages/superadmin/SupportToolsPage';
import SuperAdminSupportAccessPage from '@/pages/superadmin/SupportAccessPage';
import AuditLogsPage from '@/pages/superadmin/AuditLogsPage';
import SettingsPage from '@/pages/superadmin/SettingsPage';
import CurrenciesPage from '@/pages/superadmin/CurrenciesPage';
import WebhooksPage from '@/pages/superadmin/WebhooksPage';
import WebhookDetailPage from '@/pages/superadmin/WebhookDetailPage';
import ThemeTutorialPage from '@/pages/superadmin/ThemeTutorialPage';
import ModulePricingPage from '@/pages/superadmin/ModulePricingPage';
import SubscriptionManagementPage from '@/pages/superadmin/SubscriptionManagementPage';
import InvoicesPage from '@/pages/superadmin/InvoicesPage';
import InvoiceDetailPage from '@/pages/superadmin/InvoiceDetailPage';
import InvoiceSettingsPage from '@/pages/superadmin/InvoiceSettingsPage';
import EventDashboardPage from '@/pages/superadmin/EventDashboardPage';
import SystemHealthPage from '@/pages/superadmin/SystemHealthPage';
import JobDashboardPage from '@/pages/superadmin/JobDashboardPage';
import EmailDashboardPage from '@/pages/superadmin/EmailDashboardPage';
import SecurityOverviewPage from '@/pages/superadmin/SecurityOverviewPage';
import FeatureFlagsPage from '@/pages/superadmin/FeatureFlagsPage';
import ModuleManagementPage from '@/pages/superadmin/ModuleManagementPage';
import TemplateCatalogPage from '@/pages/superadmin/TemplateCatalogPage';
import SubscriptionLifecyclePage from '@/pages/superadmin/SubscriptionLifecyclePage';
import MigrationWizardPage from '@/pages/superadmin/MigrationWizardPage';
import DataIntegrityPage from '@/pages/superadmin/DataIntegrityPage';
import RoleTemplatesPage from '@/pages/superadmin/RoleTemplatesPage';
import JournalExplorerPage from '@/pages/superadmin/JournalExplorerPage';
import TrialBalancePage from '@/pages/superadmin/TrialBalancePage';
import LedgerAccountsPage from '@/pages/superadmin/LedgerAccountsPage';
import BalanceVerificationPage from '@/pages/superadmin/BalanceVerificationPage';
const FeatureAddonsPage = lazy(() => import('./pages/superadmin/FeatureAddonsPage'));
const UatReviewPage = lazy(() => import('./pages/superadmin/UatReviewPage'));
const TenantDataPage = lazy(() => import('./pages/superadmin/TenantDataPage'));

// Legal Pages (public - no auth required)
import PrivacyPage from '@/pages/legal/PrivacyPage';
import TermsPage from '@/pages/legal/TermsPage';

// Payment Result Page (standalone - no layout)
import PaymentResultPage from '@/pages/payment/PaymentResultPage';

// Demo Pages (temporary)
import ModuleSwitcherDemo from '@/pages/demo/ModuleSwitcherDemo';
import LoaderDemoPage from '@/pages/demo/LoaderDemoPage'; // TODO: remove after review

// Security
const AccessViolationPage = lazy(() => import('@/pages/AccessViolationPage'));

/** Renders the CoreXLoader overlay when any slow transaction is active */
function GlobalSlowOverlay() {
  const ctx = useContext(SlowOverlayContext);
  if (!ctx?.isVisible) return null;
  return <CoreXLoader variant="overlay" />;
}

function App() {
  const { isAuthenticated, user } = useAuthStore();
  const defaultRoute = user?.role === 'SUPER_ADMIN' ? '/superadmin' : '/venta';

  // Set favicon and page title based on module configuration
  useFavicon();

  // Replace browser's native constraint validation tooltips with themed inline errors
  useGlobalValidationInterceptor();

  return (
    <SlowOverlayProvider>
      <GlobalSlowOverlay />

      {/* Initialize currency cache when user is authenticated */}
      <CurrencyInitializer />

      {/* Refresh the stored user (incl. day-scope flag) from /auth/me on load/login */}
      <SessionRefresher />

      <Routes>
      {/* Public Auth Routes */}
      <Route
        path="/auth/bootstrap"
        element={<BootstrapPage />}
      />
      <Route
        path="/auth/login"
        element={isAuthenticated ? <Navigate to={defaultRoute} replace /> : <LoginPage />}
      />
      <Route
        path="/auth/mfa/setup"
        element={<MFASetupPage />}
      />
      <Route
        path="/auth/mfa/verify"
        element={<MFAVerifyPage />}
      />
      <Route
        path="/auth/mfa/setup-required"
        element={<MfaSetupRequiredPage />}
      />
      <Route
        path="/auth/password/request"
        element={<PasswordResetRequestPage />}
      />
      <Route
        path="/auth/password/reset"
        element={<PasswordResetPage />}
      />
      <Route
        path="/auth/mfa/recommend"
        element={<MfaRecommendPage />}
      />
      <Route
        path="/auth/accept-invite"
        element={<AcceptInvitePage />}
      />

      {/* Legal Pages (public) */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Payment Result (standalone - no layout, for gateway callbacks) */}
      <Route path="/payment/result" element={<PaymentResultPage />} />

      {/* Demo: Loader preview (temporary) */}
      <Route path="/demo/loader" element={<LoaderDemoPage />} />

      {/* Legacy routes redirect */}
      <Route path="/login" element={<Navigate to="/auth/login" replace />} />
      <Route path="/dashboard" element={<Navigate to={defaultRoute} replace />} />

      {/* Access Violation Page (authenticated, no layout) */}
      <Route element={<ProtectedRoute />}>
        <Route path="/access-violation" element={
          <Suspense fallback={<CoreXLoader variant="fullpage" />}>
            <AccessViolationPage />
          </Suspense>
        } />
      </Route>

      {/* Protected Business Module Routes */}
      <Route element={<ProtectedRoute />}>
        {/* VENTA - Inventory & Warehouse Module */}
        <Route element={<AppLayout />}>
          <Route path="/venta/*" element={
            <Suspense fallback={<CoreXLoader variant="fullpage" />}>
              <VentaModule />
            </Suspense>
          } />
        </Route>
      </Route>

      {/* Protected SuperAdmin Routes — RoleGuard enforces SUPER_ADMIN role */}
      <Route element={<ProtectedRoute />}>
        <Route element={<RoleGuard role="SUPER_ADMIN" />}>
        <Route element={<AppLayout />}>
          <Route element={<SuperAdminLayout />}>
          {/* Dashboard */}
          <Route path="/superadmin" element={<SuperAdminDashboard />} />

          {/* Organizations */}
          <Route path="/superadmin/organizations" element={<OrganizationsPage />} />
          <Route path="/superadmin/organizations/new" element={<CreateOrganizationPage />} />
          <Route path="/superadmin/organizations/:id" element={<OrganizationDetailPage />} />
          <Route path="/superadmin/organizations/:id/edit" element={<EditOrganizationPage />} />
          <Route path="/superadmin/organizations/:id/subscription" element={<SubscriptionManagementPage />} />

          {/* Registrations */}
          <Route path="/superadmin/registrations" element={<RegistrationsPage />} />

          {/* ARCHIVED: Legacy subscription plans replaced by modular pricing */}
          {/* <Route path="/superadmin/plans" element={<PlansPage />} /> */}
          {/* <Route path="/superadmin/plans/:id/edit" element={<EditPlanPage />} /> */}

          {/* Module Pricing (Modular Subscription Architecture) */}
          <Route path="/superadmin/module-pricing" element={<ModulePricingPage />} />

          {/* Invoices */}
          <Route path="/superadmin/invoices" element={<InvoicesPage />} />
          <Route path="/superadmin/invoices/:id" element={<InvoiceDetailPage />} />

          {/* Payments */}
          <Route path="/superadmin/payments" element={<PaymentsPage />} />

          {/* Subscription Lifecycle */}
          <Route path="/superadmin/subscription-lifecycle" element={<SubscriptionLifecyclePage />} />

          {/* Feature Add-Ons */}
          <Route path="/superadmin/addons" element={<FeatureAddonsPage />} />

          {/* Partners */}
          <Route path="/superadmin/partners" element={<PartnersPage />} />
          <Route path="/superadmin/partners/new" element={<CreatePartnerPage />} />
          <Route path="/superadmin/partners/:id" element={<PartnerDetailPage />} />
          <Route path="/superadmin/partners/:id/edit" element={<EditPartnerPage />} />

          {/* Withdrawals */}
          <Route path="/superadmin/withdrawals" element={<WithdrawalsPage />} />

          {/* Commissions */}
          <Route path="/superadmin/commissions" element={<CommissionsPage />} />

          {/* Invite Codes */}
          <Route path="/superadmin/invite-codes" element={<InviteCodesPage />} />

          {/* Support Tools */}
          <Route path="/superadmin/support" element={<SupportToolsPage />} />
          <Route path="/superadmin/support-access" element={<SuperAdminSupportAccessPage />} />

          {/* Audit Logs */}
          <Route path="/superadmin/audit-logs" element={<AuditLogsPage />} />

          {/* Webhooks */}
          <Route path="/superadmin/webhooks" element={<WebhooksPage />} />
          <Route path="/superadmin/webhooks/:id" element={<WebhookDetailPage />} />

          {/* Event Dashboard */}
          <Route path="/superadmin/events" element={<EventDashboardPage />} />

          {/* System Monitoring */}
          <Route path="/superadmin/system-health" element={<SystemHealthPage />} />
          <Route path="/superadmin/jobs" element={<JobDashboardPage />} />
          <Route path="/superadmin/emails" element={<EmailDashboardPage />} />
          <Route path="/superadmin/security" element={<SecurityOverviewPage />} />
          <Route path="/superadmin/feature-flags" element={<FeatureFlagsPage />} />
          <Route path="/superadmin/modules" element={<ModuleManagementPage />} />

          {/* Settings */}
          <Route path="/superadmin/settings" element={<SettingsPage />} />
          <Route path="/superadmin/currencies" element={<CurrenciesPage />} />
          <Route path="/superadmin/invoice-settings" element={<InvoiceSettingsPage />} />
          <Route path="/superadmin/template-catalog" element={<TemplateCatalogPage />} />
          <Route path="/superadmin/migration" element={<MigrationWizardPage />} />
          <Route path="/superadmin/data-integrity" element={<DataIntegrityPage />} />
          <Route path="/superadmin/tenant-data" element={
            <Suspense fallback={<CoreXLoader variant="fullpage" />}>
              <TenantDataPage />
            </Suspense>
          } />
          <Route path="/superadmin/role-templates" element={<RoleTemplatesPage />} />

          {/* Journal Ledger Audit */}
          <Route path="/superadmin/journal" element={<JournalExplorerPage />} />
          <Route path="/superadmin/journal/trial-balance" element={<TrialBalancePage />} />
          <Route path="/superadmin/journal/accounts" element={<LedgerAccountsPage />} />
          <Route path="/superadmin/journal/verify" element={<BalanceVerificationPage />} />

          {/* UAT Review */}
          <Route path="/superadmin/uat-review" element={
            <Suspense fallback={<CoreXLoader variant="fullpage" />}>
              <UatReviewPage />
            </Suspense>
          } />

          {/* Theme Tutorial Demo */}
          <Route path="/superadmin/theme-tutorial" element={<ThemeTutorialPage />} />

          {/* Temporary: Module Switcher Pattern Demo */}
          <Route path="/superadmin/module-switcher-demo" element={<ModuleSwitcherDemo />} />
          </Route>
        </Route>
        </Route>
      </Route>

      {/* Root redirect */}
      <Route path="/" element={<Navigate to={defaultRoute} replace />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/auth/login" replace />} />
    </Routes>
    </SlowOverlayProvider>
  );
}

export default App;
